import React from "react";
import { Box } from "@mui/material";

/**
 * Keywords to highlight in eval output with their colors.
 * Extend this list to add more keywords.
 */
export const KEYWORD_COLORS: Record<string, string> = {
  // Success indicators - green
  true: "#2e7d32",
  success: "#2e7d32",
  passed: "#2e7d32",
  valid: "#2e7d32",
  yes: "#2e7d32",
  // Failure indicators - red
  false: "#d32f2f",
  failed: "#d32f2f",
  fail: "#d32f2f",
  error: "#d32f2f",
  invalid: "#d32f2f",
  no: "#d32f2f",
};

/**
 * Get color for a value based on keywords.
 * Returns the color string or undefined if no match.
 */
export function getValueColor(value: string): string | undefined {
  return KEYWORD_COLORS[value.toLowerCase()];
}

/**
 * Colorizes a value based on keywords.
 */
function colorizeValue(value: unknown, keyIndex: number): React.ReactNode {
  const strValue = String(value);
  const lowerValue = strValue.toLowerCase();
  const color = KEYWORD_COLORS[lowerValue];

  if (color) {
    return (
      <Box
        component="span"
        key={`val-${keyIndex}`}
        sx={{ color, fontWeight: 600 }}
      >
        {strValue}
      </Box>
    );
  }
  return strValue;
}

/**
 * Colorizes eval output by formatting as key: value pairs on separate lines.
 * Returns a React element with colored values for matching keywords.
 */
export function colorizeEval(evalOutput: unknown): React.ReactNode {
  if (evalOutput === null || evalOutput === undefined) {
    return "-";
  }

  // Parse if string
  let obj = evalOutput;
  if (typeof evalOutput === "string") {
    try {
      obj = JSON.parse(evalOutput);
    } catch {
      return evalOutput;
    }
  }

  // If it's an object, format as key: value lines
  if (typeof obj === "object" && obj !== null && !Array.isArray(obj)) {
    const entries = Object.entries(obj as Record<string, unknown>);
    return (
      <>
        {entries.map(([key, value], idx) => (
          <Box key={key}>
            {key}: {colorizeValue(value, idx)}
            {idx < entries.length - 1 ? "\n" : ""}
          </Box>
        ))}
      </>
    );
  }

  // Fallback for non-objects
  return String(obj);
}
